import { notifUpdateAvailable } from "@/core/utils/func/notifApp";
import { useEffect, useRef } from "react";
import { z } from "zod";

const versionInfoSchema = z.object({
  version: z.string(),
  buildTime: z.string(),
  timestamp: z.number(),
  hash: z.string().min(1),
});
type VersionInfo = z.infer<typeof versionInfoSchema>;

const VERSION_CHECK_INTERVAL = 60_000;
const INITIAL_CHECK_DELAY = 5_000;
const VERSION_STORAGE_KEY = "app_version";
/** Délai minimal avant de rappeler le toast de mise à jour (5 min). */
const APP_UPDATE_REMINDER_INTERVAL = 5 * 60 * 1000;
const NAVIGATION_GUARD_DELAY = 1_000;

/** Clés localStorage à conserver lors du reload post-mise à jour. */
const IMPORTANT_STORAGE_KEYS = [
  "flash-pay-session",
  "flash-pay-ui",
  "i18nextLng",
] as const;

const clearCacheAndReload = async () => {
  if ("caches" in window) {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((name) => caches.delete(name)));
  }

  const keysToKeep: Record<string, string> = {};
  for (const key of IMPORTANT_STORAGE_KEYS) {
    const value = localStorage.getItem(key);
    if (value) keysToKeep[key] = value;
  }

  localStorage.clear();

  for (const [key, value] of Object.entries(keysToKeep)) {
    localStorage.setItem(key, value);
  }

  window.location.reload();
};

const fetchVersion = async (): Promise<VersionInfo | null> => {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
    });
    if (!response.ok) return null;
    const payload: unknown = await response.json();
    const parsed = versionInfoSchema.safeParse(payload);
    if (!parsed.success) return null;
    return parsed.data;
  } catch (error) {
    console.warn("Erreur lors de la vérification de la version:", error);
    return null;
  }
};

/**
 * Polling de `dist/version.json` (généré au build) pour détecter un nouveau
 * déploiement. Désactivé en DEV. Toast sonner via `notifUpdateAvailable`.
 */
export function useVersionCheck() {
  const isInitializedRef = useRef(false);
  const lastNotifiedAtRef = useRef(0);
  const isReloadingRef = useRef(false);
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const initialCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const isNavigatingRef = useRef(false);
  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const currentPathRef = useRef(
    typeof window !== "undefined" ? window.location.pathname : ""
  );

  useEffect(() => {
    if (import.meta.env.DEV) return;

    const handleNavigation = () => {
      if (currentPathRef.current !== window.location.pathname) {
        isNavigatingRef.current = true;
        currentPathRef.current = window.location.pathname;
        if (navigationTimeoutRef.current) {
          clearTimeout(navigationTimeoutRef.current);
        }
        navigationTimeoutRef.current = setTimeout(() => {
          isNavigatingRef.current = false;
          navigationTimeoutRef.current = null;
        }, NAVIGATION_GUARD_DELAY);
      }
    };

    window.addEventListener("popstate", handleNavigation);
    const pathCheckInterval = setInterval(handleNavigation, 100);

    return () => {
      window.removeEventListener("popstate", handleNavigation);
      clearInterval(pathCheckInterval);
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (import.meta.env.DEV) return;

    const checkVersion = async (isInitialCheck = false) => {
      if (isNavigatingRef.current || isReloadingRef.current) return;

      const versionInfo = await fetchVersion();
      if (!versionInfo) return;

      const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);
      const newVersionHash = versionInfo.hash;

      if (!storedVersion) {
        localStorage.setItem(VERSION_STORAGE_KEY, newVersionHash);
        isInitializedRef.current = true;
        return;
      }

      const canRemind =
        Date.now() - lastNotifiedAtRef.current > APP_UPDATE_REMINDER_INTERVAL;

      if (
        storedVersion !== newVersionHash &&
        canRemind &&
        !isInitialCheck &&
        !isNavigatingRef.current
      ) {
        lastNotifiedAtRef.current = Date.now();
        notifUpdateAvailable({
          onReload: () => {
            isReloadingRef.current = true;
            void clearCacheAndReload();
          },
        });
      }

      if (!isInitializedRef.current) {
        isInitializedRef.current = true;
      }
    };

    initialCheckTimeoutRef.current = setTimeout(() => {
      void checkVersion(true);
    }, INITIAL_CHECK_DELAY);

    checkIntervalRef.current = setInterval(() => {
      void checkVersion(false);
    }, VERSION_CHECK_INTERVAL);

    return () => {
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
      if (initialCheckTimeoutRef.current) {
        clearTimeout(initialCheckTimeoutRef.current);
      }
    };
  }, []);
}
